import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { axiosInstance } from '../../App';
import {
  Clock,
  MapPin,
  User,
  Wrench,
  Calendar,
  CheckCircle,
  PlayCircle,
  PauseCircle,
  AlertCircle,
  FileText,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Truck,
  Timer,
  DollarSign,
  Star
} from 'lucide-react';
import { formatDate, formatDateTime, formatTime } from '../../utils/dateUtils';

export default function MyJobs({ customerData }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedJob, setExpandedJob] = useState(null);
  const [jobDetails, setJobDetails] = useState({});
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [feedbackForm, setFeedbackForm] = useState({ rating: 5, comment: '' });

  useEffect(() => {
    if (customerData?.id) {
      fetchJobs();
    }
  }, [customerData]);

  const fetchJobs = async () => {
    try {
      const response = await axiosInstance.get(`/jobs?customer_id=${customerData.id}`);
      setJobs(response.data);
    } catch (error) {
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const fetchJobDetails = async (jobId) => {
    try {
      const [detailsRes, progressRes, timelineRes] = await Promise.all([
        axiosInstance.get(`/jobs/${jobId}/full-details`),
        axiosInstance.get(`/job-progress/${jobId}`),
        axiosInstance.get(`/job-timeline/${jobId}`)
      ]);
      
      setJobDetails(prev => ({ 
        ...prev, 
        [jobId]: {
          ...detailsRes.data,
          progress: progressRes.data,
          timeline: timelineRes.data
        }
      }));
    } catch (error) {
      console.error('Failed to load job details:', error);
    }
  };

  const toggleJobExpand = async (jobId) => {
    if (expandedJob === jobId) {
      setExpandedJob(null);
    } else {
      setExpandedJob(jobId);
      if (!jobDetails[jobId]) {
        await fetchJobDetails(jobId);
      }
    }
  };

  const handleSubmitFeedback = async (e) => {
    e.preventDefault();
    if (!selectedJob) return;

    try {
      // Submit feedback
      await axiosInstance.post('/feedback', {
        job_id: selectedJob.id,
        customer_id: customerData.id,
        handyman_id: selectedJob.handyman_id,
        rating: parseInt(feedbackForm.rating),
        comment: feedbackForm.comment
      });
      
      // Mark job as feedback submitted
      await axiosInstance.patch(`/jobs/${selectedJob.id}`, {
        feedback_submitted: true,
        awaiting_feedback: false,
        customer_rating: parseInt(feedbackForm.rating)
      });
      
      toast.success('Thank you for your feedback!');
      setShowFeedbackDialog(false);
      setSelectedJob(null);
      setFeedbackForm({ rating: 5, comment: '' });
      fetchJobs();
    } catch (error) {
      toast.error('Failed to submit feedback');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500',
      assigned: 'bg-purple-500',
      on_the_way: 'bg-indigo-500',
      in_progress: 'bg-orange-500',
      paused: 'bg-gray-500',
      completed: 'bg-green-500',
      cancelled: 'bg-red-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="h-4 w-4" />;
      case 'assigned': return <User className="h-4 w-4" />;
      case 'on_the_way': return <Truck className="h-4 w-4" />;
      case 'in_progress': return <PlayCircle className="h-4 w-4" />;
      case 'paused': return <PauseCircle className="h-4 w-4" />;
      case 'completed': return <CheckCircle className="h-4 w-4" />;
      case 'cancelled': return <AlertCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const getTimelineIcon = (eventType) => {
    switch (eventType) {
      case 'job_created': return <FileText className="h-4 w-4 text-blue-500" />;
      case 'handyman_assigned': return <User className="h-4 w-4 text-purple-500" />;
      case 'started_travel': return <Truck className="h-4 w-4 text-indigo-500" />;
      case 'arrived': return <MapPin className="h-4 w-4 text-teal-500" />;
      case 'started_job': return <PlayCircle className="h-4 w-4 text-orange-500" />;
      case 'paused_job': return <PauseCircle className="h-4 w-4 text-gray-500" />;
      case 'resumed_job': return <PlayCircle className="h-4 w-4 text-orange-500" />;
      case 'completed_job': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'progress_update': return <Timer className="h-4 w-4 text-blue-500" />;
      default: return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const formatEventType = (eventType) => {
    const labels = {
      job_created: 'Job Created',
      handyman_assigned: 'Handyman Assigned',
      started_travel: 'Technician En Route',
      arrived: 'Technician Arrived',
      started_job: 'Work Started',
      paused_job: 'Work Paused',
      resumed_job: 'Work Resumed',
      completed_job: 'Job Completed',
      progress_update: 'Progress Update'
    };
    return labels[eventType] || eventType.replace(/_/g, ' ').toUpperCase();
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', { timeZone: 'America/Phoenix',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return null;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffMs = end - start;
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  // Separate active and completed jobs
  const activeJobs = jobs.filter(j => !['completed', 'cancelled'].includes(j.status));
  const completedJobs = jobs.filter(j => ['completed', 'cancelled'].includes(j.status));

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            My Jobs
          </h2>
          <p className="text-gray-600">Track your service requests and job progress</p>
        </div>
      </div>

      {/* Active Jobs */}
      {activeJobs.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <PlayCircle className="h-5 w-5 text-orange-500" />
            Active Jobs ({activeJobs.length})
          </h3>
          {activeJobs.map((job) => (
            <Card key={job.id} className="backdrop-blur-sm bg-white/90 shadow-lg border-0 overflow-hidden">
              <CardHeader 
                className="cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => toggleJobExpand(job.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Wrench className="h-5 w-5 text-teal-600" />
                      {job.service_name || 'Service Job'}
                    </CardTitle>
                    <CardDescription className="flex items-center gap-4">
                      <span className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        {job.address || 'Address not set'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {formatDateTime(job.scheduled_date)}
                      </span>
                    </CardDescription>
                    {/* Progress Bar */}
                    {job.progress_percentage > 0 && (
                      <div className="mt-2">
                        <div className="flex justify-between text-xs text-gray-500 mb-1">
                          <span>Progress</span>
                          <span>{job.progress_percentage}%</span>
                        </div>
                        <div className="w-48 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-teal-500 h-2 rounded-full transition-all" 
                            style={{ width: `${job.progress_percentage}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={`${getStatusColor(job.status)} text-white flex items-center gap-1`}>
                      {getStatusIcon(job.status)}
                      {job.status.replace('_', ' ').toUpperCase()}
                    </Badge>
                    {expandedJob === job.id ? <ChevronUp /> : <ChevronDown />}
                  </div>
                </div>
              </CardHeader>

              {expandedJob === job.id && (
                <CardContent className="border-t bg-gray-50/50">
                  {jobDetails[job.id] ? (
                    <div className="space-y-6 pt-4">
                      {/* Job Info Grid */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {job.handyman_name && (
                          <div className="bg-white p-3 rounded-lg shadow-sm">
                            <div className="text-xs text-gray-500 mb-1">Technician</div>
                            <div className="font-medium flex items-center gap-1">
                              <User className="h-4 w-4 text-purple-500" />
                              {job.handyman_name}
                            </div>
                          </div>
                        )}
                        {job.estimated_hours && (
                          <div className="bg-white p-3 rounded-lg shadow-sm">
                            <div className="text-xs text-gray-500 mb-1">Est. Duration</div>
                            <div className="font-medium flex items-center gap-1">
                              <Timer className="h-4 w-4 text-blue-500" />
                              {job.estimated_hours}h
                            </div>
                          </div>
                        )}
                        {job.total_amount && (
                          <div className="bg-white p-3 rounded-lg shadow-sm">
                            <div className="text-xs text-gray-500 mb-1">Total Amount</div>
                            <div className="font-medium flex items-center gap-1">
                              <DollarSign className="h-4 w-4 text-green-500" />
                              ${job.total_amount}
                            </div>
                          </div>
                        )}
                        {job.started_at && (
                          <div className="bg-white p-3 rounded-lg shadow-sm">
                            <div className="text-xs text-gray-500 mb-1">Started At</div>
                            <div className="font-medium text-sm">
                              {formatDateTime(job.started_at)}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Progress Updates */}
                      {jobDetails[job.id].progress && jobDetails[job.id].progress.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                            <Timer className="h-4 w-4" />
                            Progress Updates ({jobDetails[job.id].progress.length})
                          </h4>
                          <div className="space-y-3">
                            {jobDetails[job.id].progress.map((update, idx) => (
                              <div key={idx} className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-teal-500">
                                <div className="flex justify-between items-start mb-2">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <User className="h-4 w-4 text-purple-500" />
                                      <span className="text-sm font-medium">{update.handyman_name || 'Technician'}</span>
                                    </div>
                                    <p className="text-gray-700">{update.work_completed}</p>
                                    {update.notes && (
                                      <p className="text-sm text-gray-500 mt-1">{update.notes}</p>
                                    )}
                                  </div>
                                  <span className="text-xs text-gray-500 whitespace-nowrap">
                                    {formatDateTime(update.created_at)}
                                  </span>
                                </div>
                                
                                {/* Progress bar */}
                                {update.percentage_complete > 0 && (
                                  <div className="mb-3">
                                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                                      <span>Progress</span>
                                      <span>{update.percentage_complete}%</span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                      <div 
                                        className="bg-teal-500 h-2 rounded-full transition-all" 
                                        style={{ width: `${update.percentage_complete}%` }}
                                      />
                                    </div>
                                  </div>
                                )}
                                
                                {/* Photos */}
                                {update.photos && update.photos.length > 0 && (
                                  <div>
                                    <p className="text-xs text-gray-500 mb-2">📷 Work Photos:</p>
                                    <div className="flex gap-2 flex-wrap">
                                      {update.photos.map((photo, photoIdx) => (
                                        <a 
                                          key={photoIdx} 
                                          href={photo} 
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          className="block w-20 h-20 rounded-lg overflow-hidden border hover:opacity-80 transition-opacity"
                                        >
                                          <img 
                                            src={photo} 
                                            alt={`Progress photo ${photoIdx + 1}`}
                                            className="w-full h-full object-cover"
                                            onError={(e) => { e.target.src = 'https://via.placeholder.com/80?text=Photo'; }}
                                          />
                                        </a>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Timeline */}
                      {jobDetails[job.id].timeline && jobDetails[job.id].timeline.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            Activity Timeline
                          </h4>
                          <div className="relative">
                            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                            <div className="space-y-3">
                              {jobDetails[job.id].timeline.filter(e => !e.event_type?.includes('expense')).map((event, idx) => (
                                <div key={idx} className="relative pl-10">
                                  <div className="absolute left-2 w-5 h-5 bg-white rounded-full border-2 border-gray-200 flex items-center justify-center">
                                    {getTimelineIcon(event.event_type)}
                                  </div>
                                  <div className="bg-white p-3 rounded-lg shadow-sm">
                                    <div className="flex justify-between items-center">
                                      <span className="font-medium text-sm">
                                        {formatEventType(event.event_type)}
                                      </span>
                                      <span className="text-xs text-gray-500">
                                        {formatDateTime(event.timestamp)}
                                      </span>
                                    </div>
                                    {event.notes && (
                                      <p className="text-sm text-gray-600 mt-1">{event.notes}</p>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Notes */}
                      {job.notes && (
                        <div className="bg-white p-3 rounded-lg shadow-sm">
                          <div className="text-xs text-gray-500 mb-1">Notes</div>
                          <p className="text-sm text-gray-700">{job.notes}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600"></div>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Completed Jobs */}
      {completedJobs.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-500" />
            Completed Jobs ({completedJobs.length})
          </h3>
          {completedJobs.map((job) => (
            <Card key={job.id} className="backdrop-blur-sm bg-white/80 shadow-lg border-0 overflow-hidden">
              <CardHeader 
                className="cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => toggleJobExpand(job.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Wrench className="h-5 w-5 text-gray-500" />
                      {job.service_name || 'Service Job'}
                    </CardTitle>
                    <CardDescription className="flex items-center gap-4">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {formatDateTime(job.completed_at || job.scheduled_date)}
                      </span>
                      {job.handyman_name && (
                        <span className="flex items-center gap-1">
                          <User className="h-4 w-4" />
                          {job.handyman_name}
                        </span>
                      )}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={`${getStatusColor(job.status)} text-white flex items-center gap-1`}>
                      {getStatusIcon(job.status)}
                      {job.status.replace('_', ' ').toUpperCase()}
                    </Badge>
                    {job.status === 'completed' && job.handyman_id && (job.awaiting_feedback || !job.feedback_submitted) && (
                      <Button
                        size="sm"
                        className="bg-yellow-500 hover:bg-yellow-600 text-white"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedJob(job);
                          setShowFeedbackDialog(true);
                        }}
                      >
                        <Star className="h-4 w-4 mr-1" />
                        Rate Service
                      </Button>
                    )}
                    {job.feedback_submitted && (
                      <Badge className="bg-green-100 text-green-700">
                        ✓ Rated
                      </Badge>
                    )}
                    {expandedJob === job.id ? <ChevronUp /> : <ChevronDown />}
                  </div>
                </div>
              </CardHeader>

              {expandedJob === job.id && (
                <CardContent className="border-t bg-gray-50/50">
                  {jobDetails[job.id] ? (
                    <div className="space-y-4 pt-4">
                      {/* Summary */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {job.started_at && job.completed_at && (
                          <div className="bg-white p-3 rounded-lg shadow-sm">
                            <div className="text-xs text-gray-500 mb-1">Duration</div>
                            <div className="font-medium">
                              {calculateDuration(job.started_at, job.completed_at)}
                            </div>
                          </div>
                        )}
                        {job.total_amount && (
                          <div className="bg-white p-3 rounded-lg shadow-sm">
                            <div className="text-xs text-gray-500 mb-1">Total Paid</div>
                            <div className="font-medium text-green-600">${job.total_amount}</div>
                          </div>
                        )}
                        {job.handyman_name && (
                          <div className="bg-white p-3 rounded-lg shadow-sm">
                            <div className="text-xs text-gray-500 mb-1">Technician</div>
                            <div className="font-medium">{job.handyman_name}</div>
                          </div>
                        )}
                      </div>

                      {/* Completion Notes */}
                      {job.completion_notes && (
                        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                          <h4 className="font-semibold text-green-800 mb-2">Completion Summary</h4>
                          <p className="text-green-700">{job.completion_notes}</p>
                        </div>
                      )}

                      {/* Final Photos */}
                      {job.final_photos && job.final_photos.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-gray-700 mb-3">📷 Final Work Photos</h4>
                          <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                            {job.final_photos.map((photo, idx) => (
                              <a 
                                key={idx} 
                                href={photo} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="block aspect-square rounded-lg overflow-hidden border hover:opacity-80 transition-opacity"
                              >
                                <img 
                                  src={photo} 
                                  alt={`Final photo ${idx + 1}`}
                                  className="w-full h-full object-cover"
                                  onError={(e) => { e.target.src = 'https://via.placeholder.com/150?text=Photo'; }}
                                />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Timeline */}
                      {jobDetails[job.id].timeline && jobDetails[job.id].timeline.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-gray-700 mb-3">Job Timeline</h4>
                          <div className="space-y-2">
                            {jobDetails[job.id].timeline.filter(e => !e.event_type?.includes('expense')).slice(0, 5).map((event, idx) => (
                              <div key={idx} className="flex items-center gap-3 text-sm">
                                {getTimelineIcon(event.event_type)}
                                <span className="font-medium">{formatEventType(event.event_type)}</span>
                                <span className="text-gray-500">—</span>
                                <span className="text-gray-500">{formatDateTime(event.timestamp)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Feedback Prompt */}
                      {(job.awaiting_feedback || !job.feedback_submitted) && (
                        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                          <h4 className="font-semibold text-yellow-800 mb-2">📝 Rate Your Experience</h4>
                          <p className="text-yellow-700 text-sm mb-3">Please take a moment to rate the service. Your feedback helps us improve!</p>
                          <Button
                            onClick={() => {
                              setSelectedJob(job);
                              setShowFeedbackDialog(true);
                            }}
                            className="bg-yellow-500 hover:bg-yellow-600 text-white"
                          >
                            <Star className="h-4 w-4 mr-2" />
                            Rate Service Now
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600"></div>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Empty State */}
      {jobs.length === 0 && (
        <Card className="backdrop-blur-sm bg-white/80 shadow-lg border-0">
          <CardContent className="py-12 text-center">
            <Wrench className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">No Jobs Yet</h3>
            <p className="text-gray-500">Your service requests and jobs will appear here.</p>
          </CardContent>
        </Card>
      )}

      {/* Feedback Dialog */}
      <Dialog open={showFeedbackDialog} onOpenChange={setShowFeedbackDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rate Your Experience</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitFeedback} className="space-y-4">
            <div className="space-y-2">
              <Label>Rating</Label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setFeedbackForm({ ...feedbackForm, rating: star })}
                    className="focus:outline-none"
                  >
                    <Star
                      className={`h-8 w-8 ${
                        star <= feedbackForm.rating
                          ? 'text-yellow-400 fill-yellow-400'
                          : 'text-gray-300'
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Comments (Optional)</Label>
              <Textarea
                value={feedbackForm.comment}
                onChange={(e) => setFeedbackForm({ ...feedbackForm, comment: e.target.value })}
                placeholder="Tell us about your experience..."
                rows={4}
              />
            </div>
            <Button type="submit" className="w-full">
              Submit Feedback
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
